import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';
import { savePendingPlan, CHECKOUT_FIRST_PLANS } from '../lib/pendingPlan';
import { 
  ArrowLeft, 
  ArrowRight, 
  Loader2, 
  Mail, 
  Lock, 
  User, 
  Heart,
  Link2,
  MessageCircle,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Logo Component
const Logo = ({ className = "h-20" }) => (
  <img 
    src="/logo-curacheck.png" 
    alt="CuraCheck" 
    className={`${className} object-contain`}
  />
);

// Password Validation
const validatePassword = (password) => {
  const errors = [];
  
  if (!password) {
    return { isValid: false, errors: ['Inserisci una password'] };
  }
  
  // Check minimum length (12 characters)
  if (password.length < 12) {
    errors.push('Almeno 12 caratteri');
  }
  
  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Almeno una lettera maiuscola (A-Z)');
  }
  
  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('Almeno una lettera minuscola (a-z)');
  }
  
  // Check for number
  if (!/[0-9]/.test(password)) {
    errors.push('Almeno un numero (0-9)');
  }
  
  // Check for special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)) {
    errors.push('Almeno un carattere speciale (es. !, @, #, $, %)');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Email Validation
const validateEmail = (email) => {
  const errors = [];
  
  if (!email) {
    return { isValid: false, errors: ['Inserisci un\'email'] };
  }
  
  // Check for spaces
  if (/\s/.test(email)) {
    errors.push('L\'email non può contenere spazi');
  }
  
  // Check for @ symbol
  if (!email.includes('@')) {
    errors.push('L\'email deve contenere @');
  }
  
  // Check basic email format (allows + for aliases)
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) {
    errors.push('Formato email non valido');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    normalizedEmail: email.toLowerCase().trim() // Case insensitive
  };
};

// Name Validation (for assistito name)
const validateName = (name) => {
  const errors = [];
  
  if (!name || name.trim().length === 0) {
    errors.push('Il nome è obbligatorio');
  }
  
  // Check max length (100 characters)
  if (name && name.length > 100) {
    errors.push('Il nome non può superare 100 caratteri');
  }
  
  // Check for potential XSS (script tags, etc.)
  const xssPattern = /<[^>]*script|javascript:|on\w+=/i;
  if (xssPattern.test(name)) {
    errors.push('Il nome contiene caratteri non validi');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitizedName: name ? name.replace(/<[^>]*>/g, '').trim().substring(0, 100) : ''
  };
};

// Sanitize input to prevent XSS
const sanitizeInput = (input) => {
  if (!input) return '';
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .trim();
};

// Step Indicator
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {[...Array(totalSteps)].map((_, i) => (
      <div
        key={i}
        className={`h-2 rounded-full transition-all ${
          i < currentStep 
            ? 'w-8 bg-[#4A7C59]' 
            : i === currentStep 
              ? 'w-8 bg-[#4A7C59]' 
              : 'w-2 bg-[#E5E7EB]'
        }`}
      />
    ))}
  </div>
);

// Google Icon
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Step 1: Account Creation
const Step1Account = ({ data, setData, onNext, loading, onGoogleSignIn, googleLoading, showTrialLine }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const passwordValidation = validatePassword(data.password);
  const emailValidation = validateEmail(data.email);
  const showPasswordErrors = passwordTouched && data.password && !passwordValidation.isValid;
  const showEmailErrors = emailTouched && data.email && !emailValidation.isValid;

  // Prevent spam clicks
  const handleSubmit = async () => {
    if (isSubmitting || loading) return;
    
    // Final validation before submit
    if (!emailValidation.isValid || !passwordValidation.isValid) {
      setEmailTouched(true);
      setPasswordTouched(true);
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Normalize email to lowercase
      setData({ ...data, email: emailValidation.normalizedEmail });
      await onNext();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-[#4A7C59]" />
        </div>
        <h2 className="text-2xl font-bold text-[#2C3E50]">Crea il tuo account</h2>
        {showTrialLine && <p className="text-[#64748B] mt-2" data-testid="registration-trial-line">Inizia la tua prova gratuita</p>}
      </div>

      {/* Google Sign In Button */}
      <Button
        type="button"
        onClick={onGoogleSignIn}
        disabled={googleLoading || isSubmitting}
        className="w-full h-14 bg-white hover:bg-gray-50 text-[#2C3E50] border-2 border-[#E5E7EB] rounded-xl font-semibold text-lg flex items-center justify-center gap-3"
      >
        {googleLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Connessione a Google...
          </>
        ) : (
          <>
            <GoogleIcon />
            Registrati con Google
          </>
        )}
      </Button>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-[#E5E7EB]" />
        <span className="text-sm text-[#9CA3AF]">oppure con email</span>
        <div className="flex-1 h-px bg-[#E5E7EB]" />
      </div>

      {/* Soft trust box */}
      <div className="bg-gradient-to-r from-[#FAF9F6] to-[#F0F4F1] rounded-2xl p-3 border border-[#E5E7EB] flex items-center gap-3">
        <div className="w-10 h-10 bg-[#E8F5E9] rounded-xl flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-5 h-5 text-[#4A7C59]" />
        </div>
        <p className="text-sm text-[#64748B]">
          <span className="font-medium text-[#2C3E50]">Sicuro e privato:</span> i tuoi dati sono protetti e non verranno mai condivisi
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#2C3E50] mb-2">Email</label>
        <input
          type="email"
          value={data.email}
          onChange={(e) => setData({ ...data, email: e.target.value })}
          onBlur={() => setEmailTouched(true)}
          placeholder="la-tua@email.it"
          className={`w-full h-14 px-4 text-lg border-2 rounded-xl focus:ring-2 outline-none transition-all ${
            showEmailErrors 
              ? 'border-red-400 focus:border-red-500 focus:ring-red-200' 
              : 'border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-[#4A7C59]/20'
          }`}
          data-testid="register-email"
        />
        {showEmailErrors && (
          <div className="mt-2 space-y-1">
            {emailValidation.errors.map((error, i) => (
              <p key={i} className="text-xs text-red-500 flex items-center gap-1">
                <span>•</span> {error}
              </p>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-[#2C3E50] mb-2">
          Crea una nuova password per CuraCheck
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={data.password}
            onChange={(e) => setData({ ...data, password: e.target.value })}
            onBlur={() => setPasswordTouched(true)}
            placeholder="Minimo 12 caratteri"
            className={`w-full h-14 px-4 pr-12 text-lg border-2 rounded-xl focus:ring-2 outline-none transition-all ${
              showPasswordErrors 
                ? 'border-red-400 focus:border-red-500 focus:ring-red-200' 
                : 'border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-[#4A7C59]/20'
            }`}
            data-testid="register-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4A7C59] transition-colors"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {showPasswordErrors ? (
          <div className="mt-2 space-y-1">
            {passwordValidation.errors.map((error, i) => (
              <p key={i} className="text-xs text-red-500 flex items-center gap-1">
                <span>•</span> {error}
              </p>
            ))}
          </div>
        ) : (
          <div className="mt-2 text-xs text-[#64748B] space-y-1">
            <p className="font-medium text-[#2C3E50]">La password deve avere:</p>
            <p>• Almeno 12 caratteri</p>
            <p>• Maiuscola, minuscola, numero e carattere speciale</p>
            <p className="text-[#9CA3AF] italic mt-1">Consiglio: usa parole casuali facili da ricordare</p>
          </div>
        )}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={loading || isSubmitting || !emailValidation.isValid || !passwordValidation.isValid}
        className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg shadow-lg shadow-[#4A7C59]/20 hover:shadow-xl transition-all disabled:opacity-50 disabled:shadow-none"
      >
        {(loading || isSubmitting) ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Registrati'}
        {!(loading || isSubmitting) && <ArrowRight className="w-5 h-5 ml-2" />}
      </Button>
    </div>
  );
};

// Step 2: Family Data
const Step2Family = ({ data, setData, onNext, onBack }) => {
  const [assistitoTouched, setAssistitoTouched] = useState(false);
  const [assistenteTouched, setAssistenteTouched] = useState(false);
  
  const assistitoValidation = validateName(data.nomeAssistito);
  const assistenteValidation = validateName(data.nomeAssistente);
  
  const showAssistitoErrors = assistitoTouched && !assistitoValidation.isValid;
  const showAssistenteErrors = assistenteTouched && !assistenteValidation.isValid;
  
  // Sanitize input on change
  const handleAssistitoChange = (e) => {
    const value = e.target.value;
    // Limit to 100 chars and basic sanitization
    const sanitized = value.substring(0, 100);
    setData({ ...data, nomeAssistito: sanitized });
  };
  
  const handleAssistenteChange = (e) => {
    const value = e.target.value;
    const sanitized = value.substring(0, 100);
    setData({ ...data, nomeAssistente: sanitized });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#FCE4EC] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Heart className="w-8 h-8 text-[#E57373]" />
        </div>
        <h2 className="text-2xl font-bold text-[#2C3E50]">Chi vuoi monitorare?</h2>
        <p className="text-[#64748B] mt-2">Inserisci i dati del tuo familiare</p>
      </div>

      {/* Soft info box */}
      <div className="bg-gradient-to-r from-[#E8F5E9] to-[#F0F9F1] rounded-2xl p-4 border border-[#C8E6C9]">
        <p className="text-sm text-[#4A7C59] text-center">
          Questi dati appariranno nelle notifiche WhatsApp per identificare facilmente le attività
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#2C3E50] mb-2">
          Nome del tuo caro
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
          <input
            type="text"
            value={data.nomeAssistito}
            onChange={handleAssistitoChange}
            onBlur={() => setAssistitoTouched(true)}
            placeholder="Es: Maria Rossi"
            maxLength={100}
            className={`w-full h-14 pl-12 pr-4 text-lg border-2 rounded-xl focus:ring-2 outline-none transition-all ${
              showAssistitoErrors 
                ? 'border-red-400 focus:border-red-500 focus:ring-red-200' 
                : 'border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-[#4A7C59]/20'
            }`}
            data-testid="register-assistito"
          />
        </div>
        {showAssistitoErrors && (
          <div className="mt-2 space-y-1">
            {assistitoValidation.errors.map((error, i) => (
              <p key={i} className="text-xs text-red-500 flex items-center gap-1">
                <span>•</span> {error}
              </p>
            ))}
          </div>
        )}
        <p className="text-xs text-[#9CA3AF] mt-1">Max 100 caratteri</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#2C3E50] mb-2">
          Nome dell'Assistente Familiare
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
          <input
            type="text"
            value={data.nomeAssistente}
            onChange={handleAssistenteChange}
            onBlur={() => setAssistenteTouched(true)}
            placeholder="Es: Anna"
            maxLength={100}
            className={`w-full h-14 pl-12 pr-4 text-lg border-2 rounded-xl focus:ring-2 outline-none transition-all ${
              showAssistenteErrors 
                ? 'border-red-400 focus:border-red-500 focus:ring-red-200' 
                : 'border-[#E5E7EB] focus:border-[#4A7C59] focus:ring-[#4A7C59]/20'
            }`}
            data-testid="register-assistente"
          />
        </div>
        {showAssistenteErrors && (
          <div className="mt-2 space-y-1">
            {assistenteValidation.errors.map((error, i) => (
              <p key={i} className="text-xs text-red-500 flex items-center gap-1">
                <span>•</span> {error}
              </p>
            ))}
          </div>
        )}
        <p className="text-xs text-[#9CA3AF] mt-1">Max 100 caratteri</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#2C3E50] mb-2">
          Nome famiglia <span className="text-[#9CA3AF]">(opzionale)</span>
        </label>
        <input
          type="text"
          value={data.nomeFamiglia}
          onChange={(e) => setData({ ...data, nomeFamiglia: e.target.value.substring(0, 100) })}
          placeholder="Es: Famiglia Rossi"
          maxLength={100}
          className="w-full h-14 px-4 text-lg border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none transition-all"
        />
      </div>

      <div className="flex gap-4">
        <Button
          onClick={onBack}
          variant="outline"
          className="flex-1 h-14 border-2 border-[#E5E7EB] rounded-xl font-bold text-lg hover:bg-[#F9FAFB] transition-all"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Indietro
        </Button>
        <Button
          onClick={onNext}
          disabled={!assistitoValidation.isValid || !assistenteValidation.isValid}
          className="flex-1 h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg shadow-lg shadow-[#4A7C59]/20 hover:shadow-xl transition-all disabled:opacity-50 disabled:shadow-none"
        >
          Continua
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Step 3: Save Number Guide
const Step3SaveNumber = ({ onNext, onBack }) => (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <div className="w-16 h-16 bg-[#E3F2FD] rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Smartphone className="w-8 h-8 text-[#5C6BC0]" />
      </div>
      <h2 className="text-2xl font-bold text-[#2C3E50]">Importante!</h2>
      <p className="text-[#64748B] mt-2">Salva questo numero in rubrica</p>
    </div>

    <div className="bg-[#E8F5E9] rounded-2xl p-6 text-center">
      <p className="text-[#4A7C59] font-bold text-2xl mb-2">+1 415 523 8886</p>
      <p className="text-[#64748B]">Salvalo come "CuraCheck"</p>
    </div>

    <div className="bg-[#FFF8E1] rounded-2xl p-4 border border-[#FFE082]">
      <p className="text-[#F57C00] text-sm">
        <strong>Perché?</strong> Salvando il numero, le notifiche WhatsApp mostreranno 
        il nome "CuraCheck" invece di un numero sconosciuto. Più facile e rassicurante!
      </p>
    </div>

    <div className="space-y-3">
      <div className="flex items-start gap-3 p-3 bg-[#FAF9F6] rounded-xl">
        <CheckCircle2 className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
        <p className="text-[#2C3E50]">Apri la rubrica del telefono</p>
      </div>
      <div className="flex items-start gap-3 p-3 bg-[#FAF9F6] rounded-xl">
        <CheckCircle2 className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
        <p className="text-[#2C3E50]">Crea nuovo contatto "CuraCheck"</p>
      </div>
      <div className="flex items-start gap-3 p-3 bg-[#FAF9F6] rounded-xl">
        <CheckCircle2 className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
        <p className="text-[#2C3E50]">Inserisci il numero sopra</p>
      </div>
    </div>

    <div className="flex gap-4">
      <Button
        onClick={onBack}
        variant="outline"
        className="flex-1 h-14 border-2 border-[#E5E7EB] rounded-xl font-bold text-lg"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Indietro
      </Button>
      <Button
        onClick={onNext}
        className="flex-1 h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
      >
        Fatto!
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  </div>
);

// Step 4: Success - Link Generated
const Step4Success = ({ linkUrl, data, onFinish, selectedPlan, loading }) => {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${window.location.origin}${linkUrl}`;

  const copyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success('Link copiato!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const message = encodeURIComponent(
      `Ciao! Usa questo link per registrare le attività di ${data.nomeAssistito}:\n${fullUrl}\n\nNon serve installare nulla, basta cliccare il link!`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const isPaidPlan = ['standard', 'premium', 'fondatori', 'founder'].includes(selectedPlan);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-12 h-12 text-[#4A7C59]" />
        </div>
        <h2 className="text-2xl font-bold text-[#2C3E50]">Tutto pronto!</h2>
        <p className="text-[#64748B] mt-2">Ecco il link per l'Assistente Familiare</p>
      </div>

      {/* Link Box */}
      <div className="bg-[#FAF9F6] rounded-2xl p-4 border-2 border-[#4A7C59]/20">
        <p className="text-sm text-[#64748B] mb-2">Link da inviare a {data.nomeAssistente}:</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-white rounded-xl px-4 py-3 border border-[#E5E7EB] overflow-hidden">
            <p className="text-[#2C3E50] font-mono text-sm truncate">{fullUrl}</p>
          </div>
          <Button
            onClick={copyLink}
            variant="outline"
            className="h-12 px-4 rounded-xl border-2"
          >
            {copied ? <CheckCircle2 className="w-5 h-5 text-[#4A7C59]" /> : <Copy className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* WhatsApp Share Button */}
      <Button
        onClick={shareWhatsApp}
        className="w-full h-14 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl font-bold text-lg"
      >
        <MessageCircle className="w-6 h-6 mr-2" />
        Invia via WhatsApp
      </Button>

      <div className="bg-[#F0F4F1] rounded-2xl p-4">
        <p className="text-[#64748B] text-sm text-center">
          L'Assistente Familiare non deve installare nulla. 
          Basta aprire il link per iniziare a registrare le attività!
        </p>
      </div>

      <Button
        onClick={onFinish}
        disabled={loading}
        className={`w-full h-14 rounded-xl font-bold text-lg ${
          isPaidPlan 
            ? 'bg-gradient-to-r from-[#4A7C59] to-[#5DADE2] hover:from-[#3A6345] hover:to-[#4A9ED0] text-white'
            : 'border-2 border-[#4A7C59] text-[#4A7C59] bg-transparent hover:bg-[#4A7C59]/5'
        }`}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Reindirizzamento...
          </span>
        ) : isPaidPlan ? (
          <>
            Procedi al pagamento
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        ) : (
          <>
            Vai alla Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>

      {isPaidPlan && (
        <p className="text-xs text-[#64748B] text-center">
          Cancella quando vuoi
        </p>
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Main Registration Page
const RegistrationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, signIn, signInWithGoogle, supabase, setFamiglia, user, loading: authLoading } = useAuth();
  
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const selectedPlan = location.state?.plan || 'base';
  const billingPeriod = location.state?.billingPeriod || 'monthly';
  const [data, setData] = useState({
    email: '',
    password: '',
    whatsapp: '',
    nomeAssistito: '',
    nomeAssistente: '',
    nomeFamiglia: '',
    plan: selectedPlan
  });

  // MIDDLEWARE: Se utente già loggato, redirect alla Dashboard (semplificato)
  useEffect(() => {
    if (!user) savePendingPlan(selectedPlan, billingPeriod);
  }, [user, selectedPlan, billingPeriod]);

  useEffect(() => {
    if (!authLoading && user) {
      // Utente già loggato, vai alla dashboard
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Il redirect viene gestito da Supabase OAuth
    } catch (error) {
      console.error('Google sign in error:', error);
      toast.error('Errore durante l\'accesso con Google');
      setGoogleLoading(false);
    }
  };

  const handleStep1Next = async () => {
    if (data.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }

    setLoading(true);
    try {
      // 1. Registra l'utente
      const signUpResult = await signUp(data.email, data.password);
      console.log('[Registration] SignUp result:', signUpResult);
      
      if (signUpResult?.user) {
        // 2. Utente creato - ora fai login automatico per stabilire la sessione
        console.log('[Registration] User created, performing automatic sign in...');
        
        try {
          const signInResult = await signIn(data.email, data.password);
          console.log('[Registration] SignIn result:', signInResult);
          
          if (signInResult?.session) {
            toast.success('Registrazione completata!');
            // Usa navigate con un piccolo delay per permettere al context di aggiornarsi
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 500);
          } else {
            console.warn('[Registration] SignIn succeeded but no session');
            toast.success('Account creato! Effettua il login.');
            navigate('/login');
          }
        } catch (signInError) {
          console.error('[Registration] Auto sign-in failed:', signInError);
          // Se il sign-in fallisce, proviamo comunque a navigare
          // perché il signUp potrebbe aver già stabilito la sessione
          if (signUpResult?.session) {
            toast.success('Registrazione completata!');
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 500);
          } else {
            toast.success('Account creato! Effettua il login.');
            navigate('/login');
          }
        }
      } else {
        toast.error('Registrazione fallita. Riprova.');
      }
    } catch (error) {
      console.error('[Registration] Error:', error);
      if (error.message?.includes('already registered')) {
        toast.error('Email già registrata. Prova ad accedere.');
      } else {
        toast.error(error.message || 'Errore durante la registrazione');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Next = () => {
    setStep(2);
  };

  const handleStep3Next = async () => {
    setLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Sessione scaduta. Riprova.');
        setStep(0);
        return;
      }

      // Create family record via API with user_id and piano directly
      const response = await fetch(`${API_URL}/api/famiglie`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome_famiglia: data.nomeFamiglia || `Famiglia ${data.nomeAssistito.split(' ').pop()}`,
          nome_assistito: data.nomeAssistito,
          nome_assistente: data.nomeAssistente,
          whatsapp_numero: data.whatsapp,
          user_id: user.id,
          piano: selectedPlan === 'fondatori' || selectedPlan === 'founder' ? undefined : selectedPlan
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setGeneratedLink(result.url);
        setFamiglia(result.famiglia);
        setStep(3);
        toast.success('Famiglia creata con successo!');
      } else {
        throw new Error('Errore creazione famiglia');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore durante la configurazione');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    // Paid plans (Founder Club included) always go through Stripe checkout
    if (['standard', 'premium', 'fondatori', 'founder'].includes(selectedPlan)) {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        const response = await fetch(`${API_URL}/api/checkout/create-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            plan: selectedPlan,
            billing_period: billingPeriod,
            origin_url: window.location.origin,
            user_id: user?.id,
            email: data.email
          })
        });

        const checkoutData = await response.json();
        
        if (checkoutData.checkout_url) {
          window.location.href = checkoutData.checkout_url;
        } else {
          throw new Error('URL checkout non ricevuto');
        }
      } catch (error) {
        console.error('Checkout error:', error);
        toast.error('Errore nell\'avvio del pagamento. Puoi completarlo dalla dashboard.');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    } else {
      // Free plan - go directly to dashboard
      navigate('/dashboard');
    }
  };

  // Show selected plan indicator
  const getPlanLabel = () => {
    const labels = {
      premium: { monthly: 'Piano Premium - €21,00/mese', quarterly: 'Piano Premium - €54,00/trimestre', annual: 'Piano Premium - €169,90/anno' },
      standard: { monthly: 'Piano Standard - €15,00/mese', quarterly: 'Piano Standard - €38,90/trimestre', annual: 'Piano Standard - €120,00/anno' },
      fondatori: { monthly: 'Founder Club - €6,90/mese', quarterly: 'Founder Club - €6,90/mese', annual: 'Founder Club - €69,90/anno' }
    };
    const planKey = selectedPlan === 'founder' ? 'fondatori' : selectedPlan;
    if (labels[planKey]) {
      return labels[planKey][billingPeriod] || labels[planKey].monthly;
    }
    return 'Prova gratuita di 7 giorni, senza carta';
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      {/* Header */}
      <header className="p-4 sm:p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#4A7C59]">
          <ArrowLeft className="w-5 h-5" />
          <span>Torna alla home</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-4">
            <Logo className="h-24 mx-auto" />
          </div>

          {/* Step Indicator */}
          <StepIndicator currentStep={step} totalSteps={4} />

          {/* Plan Indicator */}
          {step < 3 && (
            <div className="mb-6 text-center">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                selectedPlan === 'premium' 
                  ? 'bg-gradient-to-r from-[#F59E0B]/20 to-[#EF4444]/20 text-[#D97706]'
                  : 'bg-[#E8F5E9] text-[#4A7C59]'
              }`}>
                {selectedPlan === 'premium' ? '👑' : '⭐'} {getPlanLabel()}
              </span>
            </div>
          )}

          {/* Steps */}
          {step === 0 && (
            <Step1Account 
              data={data} 
              setData={setData} 
              onNext={handleStep1Next}
              loading={loading}
              onGoogleSignIn={handleGoogleSignIn}
              googleLoading={googleLoading}
              showTrialLine={!CHECKOUT_FIRST_PLANS.includes(selectedPlan)}
            />
          )}
          {step === 1 && (
            <Step2Family 
              data={data} 
              setData={setData} 
              onNext={handleStep2Next}
              onBack={() => setStep(0)}
            />
          )}
          {step === 2 && (
            <Step3SaveNumber 
              onNext={handleStep3Next}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <Step4Success 
              linkUrl={generatedLink}
              data={data}
              onFinish={handleFinish}
              selectedPlan={selectedPlan}
              loading={loading}
            />
          )}

          {/* Login Link */}
          {step === 0 && (
            <p className="text-center mt-8 text-[#64748B]">
              Hai già un account?{' '}
              <Link to="/login" className="text-[#4A7C59] font-semibold hover:underline">
                Accedi
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default RegistrationPage;
